import { StackScreenProps } from "@react-navigation/stack";
import { RootStackParamList } from "../../navigations/RootNavigation";
import { InvitationAction, useInvitationHandler } from "../../hooks/invitations";
import React, { useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { GET_INVITATION, SEND_INVITATION } from "../../graphql/invitation";
import LoadingBoundary from "../../ui_atoms/LoadingBoundary";
import { useI18n } from "../../hooks/i18n-hooks";
import { AuthManagerContext } from "../../contexts/AuthManager";
import { InvitationMode } from "../../__generated__/graphql";
import { useNavigation } from "@react-navigation/native";
import Callout from "../../ui_atoms/Callout";
import TextField from "../../components/TextField";
import PhoneField, { PhoneFieldHandle } from "../../components/PhoneField";
import Button from "../../components/Button";
import VStack from "../../ui_atoms/VStack";
import Gap from "../../ui_atoms/Gap";
import { ScrollView, StyleSheet } from "react-native";
import { useTheme } from "../../hooks/use-theme";
import { useSnackbar } from "../../hooks/snackbar";
import Text from "../../components/Text";
import { useTracking } from "../../hooks/tracking";
import { ClientWorkersTabParamList } from "../../navigations/client/WorkersNavigation";
import apolloClient from "../../configs/graphql.config";
import { GET_CLIENT_AND_INVITATION_WORKERS } from "../../graphql/client";

type ClientInvitationDetailsProps = StackScreenProps<RootStackParamList, "ClientInvitationDetails">;

type ScreenParams = StackScreenProps<ClientWorkersTabParamList>;

const ClientInvitationDetailsScreen = ({ route }: ClientInvitationDetailsProps) => {
    const navigation = useNavigation<ScreenParams["navigation"]>();
    const i18n = useI18n();
    const styles = useStyles();
    const invitationId = route?.params?.invitationId || "";
    const { refreshUser } = useContext(AuthManagerContext);
    const {
        data,
        error: getError,
        loading: getLoading,
    } = useQuery(GET_INVITATION, {
        variables: { invitationId: invitationId },
    });
    const { showSnackbar } = useSnackbar();
    const { trackEvent } = useTracking();
    const onActionSuccess = useCallback(
        (action: InvitationAction) => {
            if (["accept", "reject", "cancel", "resend"].includes(action)) {
                void refreshUser?.();

                showSnackbar({
                    text: i18n.t(`invitation.action_result.${action}.success`),
                    mode: "success",
                });
            }
            navigation.popToTop();
        },
        [refreshUser, navigation, showSnackbar, i18n],
    );

    const {
        onAction,
        processingAction,
        error: procError,
        reset,
    } = useInvitationHandler(onActionSuccess);

    const invitation = data?.getInvitation || null;

    useEffect(() => {
        // check if invitation still exists,
        // backend return null if invitation does not exist or is not accessible by the current user
        if (data && data.getInvitation === null) {
            void apolloClient.refetchQueries({
                include: [GET_CLIENT_AND_INVITATION_WORKERS],
            });
            // notify user
            showSnackbar({
                text: i18n.t(`invitation.invitation_not_found`),
                mode: "warning",
            });
            if (navigation.canGoBack()) {
                navigation.goBack();
            } else {
                navigation.navigate("WorkerList");
            }
        }
    }, [data, i18n, navigation, showSnackbar]);

    const [sendInvitation, { loading: sendLoading, error: sendError, reset: sendReset }] =
        useMutation(SEND_INVITATION);
    const phoneInputRef = useRef<PhoneFieldHandle>(null);
    const apolloError = procError || getError || sendError || null;
    const error = apolloError
        ? i18n.t(`invitation.action_result.${processingAction || "default"}.error`)
        : null;

    const isWrongReceiver = useMemo(
        () => invitation && invitation.mode === InvitationMode.Receiver,
        [invitation],
    );

    const onSendInvitation = useCallback(async () => {
        if (invitation) {
            await sendInvitation({
                variables: {
                    phoneNumber: invitation.phoneNumber,
                    firstName: invitation.firstName,
                    lastName: invitation.lastName,
                },
            });
            void trackEvent("click_resend_invitation");
            showSnackbar({
                text: i18n.t("invitation.action_result.resend.success"),
                mode: "success",
            });

            navigation.popToTop();
        }
    }, [invitation, sendInvitation, showSnackbar, i18n, navigation, trackEvent]);

    return (
        <LoadingBoundary loading={sendLoading || getLoading} error={error} clearError={reset}>
            {invitation && (
                <ScrollView>
                    <VStack v={1 / 2} h={1 / 2} gap={1} alignment="stretch">
                        <Text style={styles.text}>{i18n.t("invitation.details.description")}</Text>
                        {error && (
                            <Callout
                                markdown={i18n.t("invitation.send.error")}
                                mode="error"
                                onClose={() => {
                                    reset();
                                    sendReset();
                                }}
                            />
                        )}
                        <TextField
                            label={i18n.t("invitation.send.firstName")}
                            disabled
                            value={invitation.firstName || ""}
                        />
                        <TextField
                            label={i18n.t("invitation.send.lastName")}
                            disabled
                            value={invitation.lastName || ""}
                        />
                        <PhoneField
                            ref={phoneInputRef}
                            initialValue={invitation.phoneNumber || ""}
                            disabled
                        />
                        {isWrongReceiver && (
                            <Callout
                                mode="error"
                                markdown={i18n.t("invitation.error_bad_recipient")}
                            />
                        )}
                        <Gap />
                        <Button
                            buttonMode="outlined"
                            onPress={async () => await onAction("cancel", invitation)}
                            label={i18n.t(`invitation.action.cancel`)}
                        />
                        <Button
                            buttonMode="secondary"
                            label={i18n.t("invitation.send.resendButton")}
                            onPress={onSendInvitation}
                            processing={sendLoading || getLoading}
                            size="large"
                        />
                    </VStack>
                </ScrollView>
            )}
        </LoadingBoundary>
    );
};

export default ClientInvitationDetailsScreen;

const useStyles = () => {
    const { colors, fonts } = useTheme();
    return useMemo(
        () =>
            StyleSheet.create({
                text: {
                    ...fonts.bodyMedium,
                    color: colors.text,
                },
            }),
        [colors.text, fonts.bodyMedium],
    );
};
